import * as React from "react";
import {useTranslation} from "react-i18next";
import {useAuth} from "../context/AuthContext";
import {usePeriod} from "../context/PeriodContext";
import type {AllPayrollPeriod} from "../services/payrollService";
import {getAllPayrollPeriods} from "../services/payrollService";
import {getRegularHours} from "../services/workEvidenceService";
import {API_BASE_URL} from "../config/apiConfig";
import {
    editableColumns,
    getComparator,
    type EditableColumn,
    type Order,
    type SortableKey,
    type WorkEvidenceRecord,
} from "../components/WorkEvidence/WorkEvidenceTableComponent";
import {getEvidenceState, isApprovedStatus} from "../components/WorkEvidence/StatusChip";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Branch {
    id: string;
    name: string;
}

interface BranchesResponse {
    totalRecords: number;
    tenants: Branch[];
}

interface WorkEvidenceResponse {
    totalRecords: number;
    workEvidences: WorkEvidenceRecord[];
}

export type StatusFilter = "ALL" | "PENDING" | "SUBMITTED" | "APPROVED";

interface WorkEvidenceDraft {
    editedCells: { [key: string]: string };
    commentDrafts: { [employeeId: string]: string };
}

function draftStorageKey(periodKey: string, branchId: string): string {
    return `nuto_work_evidence_draft:${periodKey}:${branchId}`;
}

export function useWorkEvidence() {
    const {t} = useTranslation();
    // Sorting state
    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<SortableKey>("firstName");

    const handleRequestSort = (property: SortableKey) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };
    const {
        selectedPeriod,
        selectedPeriodValue,
        dashboardBranch,
        dashboardBranchName,
    } = usePeriod();
    const {token} = useAuth();

    // Mirrors usePayrollRun.ts: the backend accepts the toolbar's "All" branch value directly
    // (branch=All), so it's passed straight through rather than special-cased.
    const selectedBranch = dashboardBranch;
    const selectedBranchName = dashboardBranchName;

    const [allPeriods, setAllPeriods] = React.useState<AllPayrollPeriod[]>([]);
    const [branches, setBranches] = React.useState<Branch[]>([]);
    const [loadingBranches, setLoadingBranches] = React.useState<boolean>(false);
    const [branchesError, setBranchesError] = React.useState<string | null>(null);

    const [workEvidenceData, setWorkEvidenceData] = React.useState<WorkEvidenceRecord[]>([]);
    const [isLoadingData, setIsLoadingData] = React.useState<boolean>(false);
    const [tableDataError, setTableDataError] = React.useState<string | null>(null);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const itemsPerPage = 5;

    const [editedCells, setEditedCells] = React.useState<{ [key: string]: string }>({});
    const [activeCellId, setActiveCellId] = React.useState<string | null>(null);
    const [commentDrafts, setCommentDrafts] = React.useState<{ [employeeId: string]: string }>({});
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    // Selection (which employees are targeted by "Dërgo për miratim")
    const [selected, setSelected] = React.useState<string[]>([]);

    // Search & status filter
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("ALL");

    const [uploading, setUploading] = React.useState<boolean>(false);
    const [uploadError, setUploadError] = React.useState<string | null>(null);

    const isPeriodOpen = selectedPeriodValue?.includes("OPEN") ?? false;
    const isPeriodLocked = selectedPeriodValue?.toUpperCase().includes("LOCKED") ?? false;

    // ─── Draft persistence (sessionStorage) ────────────────────────────────────

    const restoreDraft = (periodKey: string, branchId: string) => {
        try {
            const raw = sessionStorage.getItem(draftStorageKey(periodKey, branchId));
            if (!raw) return;
            const draft: WorkEvidenceDraft = JSON.parse(raw);
            setEditedCells(draft.editedCells ?? {});
            setCommentDrafts(draft.commentDrafts ?? {});
        } catch (error) {
            console.error("Failed to restore work evidence draft:", error);
        }
    };

    const clearDraftStorage = (periodKey: string | null, branchId: string | null) => {
        if (!periodKey || !branchId) return;
        try {
            sessionStorage.removeItem(draftStorageKey(periodKey, branchId));
        } catch (error) {
            console.error("Failed to clear work evidence draft:", error);
        }
    };

    const handleSaveDraft = () => {
        if (!selectedPeriod || !selectedBranch) return;
        try {
            sessionStorage.setItem(
                draftStorageKey(selectedPeriod, selectedBranch),
                JSON.stringify({editedCells, commentDrafts}),
            );
            setSuccessMessage(t('workEvidence.draft.saved'));
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            console.error("Failed to save work evidence draft:", error);
        }
    };

    const handleCancelEdits = () => {
        setEditedCells({});
        setCommentDrafts({});
        setActiveCellId(null);
        clearDraftStorage(selectedPeriod, selectedBranch);
    };

    // ─── Handlers ───────────────────────────────────────────────────────────────

    const fetchBranches = async () => {
        if (!token) return;

        try {
            setLoadingBranches(true);
            setBranchesError(null);

            const response = await fetch(`${API_BASE_URL}/api/branches/get`, {
                method: "GET",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch branches: ${response.statusText}`);
            }

            const data: BranchesResponse = await response.json();
            setBranches(data.tenants || []);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('hr.branches.error.fetchFailed');
            console.error("Error fetching branches:", error);
            setBranchesError(errorMessage);
            setBranches([]);
        } finally {
            setLoadingBranches(false);
        }
    };

    const fetchWorkEvidenceData = async (periodKey: string, branchId: string) => {
        if (!token) return;

        try {
            setIsLoadingData(true);
            setTableDataError(null);

            const response = await fetch(
                `${API_BASE_URL}/api/work-evidence/get?periodKey=${periodKey}&branch=${branchId}`,
                {
                    method: "GET",
                    headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                },
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch work evidence data: ${response.statusText}`);
            }

            const data: WorkEvidenceResponse = await response.json();
            setWorkEvidenceData(data.workEvidences || []);
            setCurrentPage(1);
            return data.workEvidences || [];
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : t('workEvidence.error.fetchDataFailed');
            console.error("Error fetching work evidence data:", error);
            setTableDataError(errorMessage);
            setWorkEvidenceData([]);
            return [] as WorkEvidenceRecord[];
        } finally {
            setIsLoadingData(false);
        }
    };

    // Prefill Regular hours from the backend (never computed here). Only touches editedCells for
    // unsaved rows (no workEvidenceId or saved hours null/0) that are not approved and have no existing
    // edit (incl. restored draft). Nothing is saved/submitted; failures are silent.
    const prefillRegularHours = async (periodKey: string, rows: WorkEvidenceRecord[], isCurrent: () => boolean) => {
        if (!token || rows.length === 0) return;
        try {
            const result = await getRegularHours(token, periodKey);
            if (!isCurrent()) return;
            const fullMonthHours = result?.regularHours;
            setEditedCells((prev) => {
                const next = {...prev};
                let changed = false;
                rows.forEach((row) => {
                    // Per-employee value is prorated to contract dates; full-month is only a fallback.
                    const hours = typeof row.suggestedRegularHours === "number" ? row.suggestedRegularHours : fullMonthHours;
                    if (typeof hours !== "number" || Number.isNaN(hours)) return;
                    const saved = row.regularHours;
                    const eligible = row.workEvidenceId == null || saved == null || saved === 0;
                    const cellId = `${row.employeeId}-regularHours`;
                    if (eligible && !isApprovedStatus(row.status) && next[cellId] === undefined) {
                        next[cellId] = String(hours);
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        } catch (error) {
            console.error("Failed to prefill regular hours:", error);
        }
    };

    const handleCellValueChange = (cellId: string, value: string) => {
        setEditedCells({...editedCells, [cellId]: value});
    };

    const handleCellActivate = (cellId: string) => setActiveCellId(cellId);
    const handleCellBlur = () => setActiveCellId(null);

    const handleCommentChange = (employeeId: string, value: string) => {
        setCommentDrafts((prev) => ({...prev, [employeeId]: value}));
    };

    // ─── Selection ──────────────────────────────────────────────────────────────

    const isSelected = (id: string) => selected.includes(id);

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelected(filteredData.map((r) => r.employeeId));
        } else {
            setSelected([]);
        }
    };

    const handleRowCheckboxClick = (event: React.MouseEvent, id: string) => {
        event.stopPropagation();
        setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
    };

    // ─── Submit for approval ────────────────────────────────────────────────────

    const buildEmployeeEdit = (record: WorkEvidenceRecord) => {
        const getValue = (column: EditableColumn) => {
            const cellId = `${record.employeeId}-${column}`;
            const edited = editedCells[cellId];
            if (edited !== undefined && edited !== "") {
                const numeric = Number.parseFloat(edited);
                return Number.isNaN(numeric) ? record[column] : numeric;
            }
            return record[column];
        };

        return {
            employeeId: record.employeeId,
            workEvidenceId: record.workEvidenceId,
            regularHours: getValue("regularHours"),
            overTimeHours: getValue("overTimeHours"),
            nightHours: getValue("nightHours"),
            weekendHours: getValue("weekendHours"),
            holidayHours: getValue("holidayHours"),
            comment: commentDrafts[record.employeeId] ?? record.comment ?? undefined,
        };
    };

    const handleSubmitForApproval = async () => {
        if (!selectedPeriod || !selectedBranch) return;

        const targetIds = selected.length > 0 ? selected : workEvidenceData.map((r) => r.employeeId);
        if (targetIds.length === 0) return;

        const edits = targetIds
            .map((employeeId) => workEvidenceData.find((r) => r.employeeId === employeeId))
            .filter((record): record is WorkEvidenceRecord => Boolean(record))
            .map(buildEmployeeEdit);

        if (edits.length === 0) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/work-evidence/add`, {
                method: "POST",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                body: JSON.stringify({periodKey: selectedPeriod, branch: selectedBranch, edits}),
            });

            if (!response.ok) {
                throw new Error(`Failed to submit work evidence: ${response.statusText}`);
            }

            const responseData = await response.json();

            if (responseData.success) {
                setEditedCells({});
                setCommentDrafts({});
                setActiveCellId(null);
                setSelected([]);
                clearDraftStorage(selectedPeriod, selectedBranch);
                setSuccessMessage(t('workEvidence.success.submitted', {count: edits.length}));
                setTimeout(() => setSuccessMessage(null), 3000);
                await fetchWorkEvidenceData(selectedPeriod, selectedBranch);
            } else {
                throw new Error(t('workEvidence.error.serverUnsuccessful'));
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('workEvidence.error.saveEditsFailed');
            console.error("Error submitting work evidence:", error);
            setTableDataError(errorMessage);
        }
    };

    // ─── Import (Excel) ─────────────────────────────────────────────────────────

    const handleImportWorkEvidence = async (file: File) => {
        if (!token) return;
        setUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${API_BASE_URL}/api/work-evidence/upload`, {
                method: "POST",
                headers: {Authorization: `Bearer ${token}`},
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Import failed: ${response.statusText}`);
            }

            const data: WorkEvidenceResponse = await response.json();
            const parsedRows = data.workEvidences || [];

            const knownEmployeeIds = new Set(workEvidenceData.map((r) => r.employeeId));
            const matchedRows = parsedRows.filter((row) => knownEmployeeIds.has(row.employeeId));

            const nextEditedCells: { [key: string]: string } = {...editedCells};
            matchedRows.forEach((row) => {
                editableColumns.forEach((column) => {
                    const value = row[column];
                    if (value !== null && value !== undefined) {
                        nextEditedCells[`${row.employeeId}-${column}`] = String(value);
                    }
                });
            });
            setEditedCells(nextEditedCells);

            if (matchedRows.length === 0) {
                setUploadError(t('workEvidence.import.noMatches'));
            } else {
                setSuccessMessage(t('workEvidence.import.success', {count: matchedRows.length}));
                setTimeout(() => setSuccessMessage(null), 3000);
                if (matchedRows.length < parsedRows.length) {
                    setUploadError(t('workEvidence.import.partialMatch', {
                        matched: matchedRows.length,
                        total: parsedRows.length,
                    }));
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('workEvidence.import.error');
            console.error("Error importing work evidence:", error);
            setUploadError(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    // ─── Effects ─────────────────────────────────────────────────────────────────

    React.useEffect(() => {
        const fetchAllPeriods = async () => {
            if (!token) return;
            try {
                const periodsResponse = await getAllPayrollPeriods(token);
                setAllPeriods(periodsResponse);
            } catch (error) {
                console.error("Failed to fetch all payroll periods:", error);
                setAllPeriods([]);
            }
        };
        fetchAllPeriods();
    }, [token]);

    React.useEffect(() => {
        fetchBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Period/branch selection now comes from the global toolbar selector (PeriodBranchToolbarSelector)
    // rather than a selector local to this page, so this effect must re-fetch whenever either
    // changes - not just once on mount.
    React.useEffect(() => {
        if (token && selectedPeriod && selectedBranch) {
            setCurrentPage(1);
            setEditedCells({});
            setCommentDrafts({});
            setActiveCellId(null);
            setSelected([]);
            let cancelled = false;
            fetchWorkEvidenceData(selectedPeriod, selectedBranch).then(async (rows) => {
                if (cancelled) return;
                restoreDraft(selectedPeriod, selectedBranch);
                await prefillRegularHours(selectedPeriod, rows ?? [], () => !cancelled);
            });
            return () => {
                cancelled = true;
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedPeriod, selectedBranch]);

    // Reset to page 1 whenever the visible dataset shrinks (search/filter change)
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    // Sort data before filtering/paginating (must be at top level for hooks rule)
    const sortableData = React.useMemo(
        () => [...workEvidenceData].sort(getComparator(order, orderBy)),
        [workEvidenceData, order, orderBy],
    );

    const filteredData = React.useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return sortableData.filter((record) => {
            const matchesSearch =
                term.length === 0 ||
                `${record.firstName} ${record.lastName}`.toLowerCase().includes(term);
            const state = getEvidenceState(record.status);
            const matchesStatus =
                statusFilter === "ALL" ||
                (statusFilter === "APPROVED" && state === "APPROVED") ||
                (statusFilter === "SUBMITTED" && state === "SUBMITTED") ||
                (statusFilter === "PENDING" && state === "IN_PREPARATION");
            return matchesSearch && matchesStatus;
        });
    }, [sortableData, searchTerm, statusFilter]);

    const stats = React.useMemo(() => {
        const total = workEvidenceData.length;
        const approved = workEvidenceData.filter((r) => getEvidenceState(r.status) === "APPROVED").length;
        const submitted = workEvidenceData.filter((r) => getEvidenceState(r.status) === "SUBMITTED").length;
        // pending = still in preparation (not yet sent for approval)
        return {total, approved, submitted, pending: total - approved - submitted};
    }, [workEvidenceData]);

    const [downloading, setDownloading] = React.useState<boolean>(false);

    const downloadWorkEvidence = async () => {
        if (!token || !selectedPeriod) return;
        setDownloading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/work-evidence/get/download?periodKey=${selectedPeriod}&branch=${selectedBranch}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Download failed: ${response.statusText}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const contentDisposition = response.headers.get("Content-Disposition");
            const filename = contentDisposition
                ? contentDisposition.split("filename=")[1]?.replace(/"/g, "") ?? "work-evidence.xsls"
                : "work-evidence.xsls";
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading work evidence:", error);
        } finally {
            setDownloading(false);
        }
    };

    return {
        order,
        orderBy,
        handleRequestSort,
        selectedPeriod,
        selectedPeriodValue,
        selectedBranch,
        selectedBranchName,
        allPeriods,
        branches,
        loadingBranches,
        branchesError,
        workEvidenceData,
        isLoadingData,
        tableDataError,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        editedCells,
        setEditedCells,
        activeCellId,
        setActiveCellId,
        commentDrafts,
        handleCommentChange,
        successMessage,
        setSuccessMessage,
        isPeriodOpen,
        isPeriodLocked,
        handleCellValueChange,
        handleCellActivate,
        handleCellBlur,
        sortableData,
        filteredData,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        stats,
        selected,
        isSelected,
        handleSelectAllClick,
        handleRowCheckboxClick,
        handleSubmitForApproval,
        handleSaveDraft,
        handleCancelEdits,
        uploading,
        uploadError,
        setUploadError,
        handleImportWorkEvidence,
        downloadWorkEvidence,
        downloading,
    };
}
